import { SxProp, View, useDripsyTheme, styled, Pressable, FlatList, ActivityIndicator } from 'dripsy';
import React, { useContext, useEffect, useState } from 'react';
import {
  AccountManagementStackRoutes,
  AccountManagementStackScreenProps,
  AccountManagementStackParamList
} from '@/features/account-management/account.navigator';
import moment from 'moment-timezone';
import { TextTitle } from '@/design-system/components/typography';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniIconButton } from '@/design-system/components/buttons';
import { Row } from '@/design-system/components/layout';
import { UniInput } from '@/design-system/components/inputs';
import { TextBody, TextSmall } from '@/design-system/components/typography';
import { useAsyncStorage } from '@/devtools/hooks/useAsyncStorage';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { GetUserAccountTransactionsForAppResponseDataItem } from '@utx/utx-client';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { PageHeaderModalNative } from '@/features/account-management/screens/page/PageHeaderModalNative';
import { keyGenerator } from '@/utils/keyGenerator';
import { AccountTransactionIntentFilters } from '@/features/account-management/options';
import { useAccountTransaction } from '@/features/account-management/screens/details/hooks/useAccount.hook';
import { LayoutContext } from 'contexts/LayoutContext';
import { RootStackRoutes } from 'types';
import { DownloadTransactionReportStackRoutes } from '@/features/account-management/screens/download-transaction-report/type';

export interface AccountTransactionHistoryProps {}

export const AccountTransactionHistoryScreen: React.FC<
  AccountManagementStackScreenProps<AccountManagementStackRoutes.AccountTransactionHistory>
> = ({ navigation, route }) => {
  const navigationAny = useNavigation<any>();
  const { account } = route.params;
  const { t } = useTranslation('account-management');
  const { value: show, set: setShow } = useAsyncStorage<boolean>('showAmount');
  const { theme } = useDripsyTheme();
  const layout = useContext(LayoutContext);
  const onGoBack = () => {
    navigation.goBack();
  };

  const isNegative = (value: string) => {
    return (value && value.toString().startsWith('-')) || false;
  };
  const hideShowBalance = (balance: string) => {
    return show ? '******' : balance;
  };
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [filterValues, setFilterValues] = useState<any>();

  const accountNavigation = useNavigation<NativeStackNavigationProp<AccountManagementStackParamList>>();
  const AccountTransactionDetail = (record: GetUserAccountTransactionsForAppResponseDataItem) => {
    accountNavigation.navigate(AccountManagementStackRoutes.AccountTransactionDetail, { record: record });
  };

  const { transactions, isLoadingAccountTransactions, nextToken, preToken, next, prev, reloadAccountTransactions, clearCurrentPageToken } =
    useAccountTransaction({
      accountId: account,
      dateRangeFrom: filterValues?.fromDate,
      dateRangeTo: filterValues?.toDate,
      search: search
    });

  const [transactionsData, setTransactionsData] = useState([]);
  useEffect(() => {
    let transactionsFlag = transactionsData;
    transactions.forEach(
      (transaction) =>
        (transactionsFlag.length &&
          !transactionsFlag.filter((tf) => tf?.intentId == transaction?.intentId).length &&
          transactionsFlag.push(transaction)) ||
        (!transactionsFlag.length && transactionsFlag.push(transaction))
    );
    setTransactionsData(transactionsFlag);
  }, [transactions, transactionsData]);

  const flatlistHight = layout.dimensions.height - 160;

  return (
    <View sx={styles.contentContainer}>
      <PageHeaderModalNative>
        <Row sx={{ justifyContent: 'space-between', alignItems: 'center', px: '$4', mt: '$5', mb: '$4' }}>
          <UniIconButton
            icon="arrowLeft"
            options={{ iconStyles: { color: theme?.colors?.$white as string } }}
            onPress={onGoBack}></UniIconButton>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('card-account-management::account-page.title--transaction-history')}
            </TextTitle>
          </View>
          <View sx={{ flexDirection: 'row', gap: '$1' }}>
            <UniIconButton
              icon="historyCalendar"
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              onPress={() => {
                navigation.navigate(AccountManagementStackRoutes.BottomSheetDatePicker, {
                  value: {
                    from: filterValues?.fromDate,
                    to: filterValues?.toDate
                  },
                  onChange: (value) => {
                    setTransactionsData([]);
                    clearCurrentPageToken();
                    setFilterValues({
                      ...filterValues,
                      fromDate: value.from?.toDate(),
                      toDate: value.to?.toDate()
                    });
                  }
                });
              }}></UniIconButton>
            <UniIconButton
              icon="exportsymbol"
              options={{ iconStyles: { color: theme?.colors?.$white as string, width: 24, height: 24 } }}
              onPress={() => {
                navigationAny.navigate(RootStackRoutes.AccountManagement, {
                  screen: AccountManagementStackRoutes.DownloadTransactionReport,
                  params: {
                    screen: DownloadTransactionReportStackRoutes.DownloadAccountTransactionReport,
                    params: { walletId: account }
                  }
                });
              }}></UniIconButton>
          </View>
        </Row>
      </PageHeaderModalNative>
      <View sx={styles.card}>
        <View sx={styles.table}>
          <View sx={styles.operations}>
            <View sx={styles.filters}>
              <UniInput
                sx={styles.filter_search}
                size="default"
                prefixIcon="search"
                placeholder={t('card-account-management::card-details.text--search-by-desc-ref-id')}
                onValueChange={(value) => {
                  setTransactionsData([]);
                  clearCurrentPageToken();
                  setSearch(value);
                }}
              />
            </View>
          </View>
          <FlatList
            data={transactionsData}
            renderItem={({ item }) => {
              return (
                <Pressable onPress={() => AccountTransactionDetail(item)} sx={{ px: '$4' }}>
                  <View sx={{ marginY: 6, gap: 20, borderRadius: 10, boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.07)' }}>
                    <View
                      sx={{
                        margin: '$4'
                      }}>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Icon name={item?.asset?.toLowerCase()} width={28} height={28} />
                          <TextBody semiBold sx={{ ml: '$2', color: '$textPrimary', width: 70 }}>
                            {AccountTransactionIntentFilters(t).find((rec) => rec.value === item?.intent)?.label}
                          </TextBody>
                        </View>
                        <TextBody semiBold success={!isNegative(item?.amount) && true} danger={isNegative(item?.amount) && true}>
                          {hideShowBalance(
                            formatAmountByDecimal({
                              value: Math.abs(item?.amount),
                              suffix: item?.asset,
                              prefix: !isNegative(item?.amount) ? '+' : '-',
                              decimals: 2
                            })
                          )}
                        </TextBody>
                      </View>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mt: '$2' }}>
                        <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Icon name="clock1" width={8} height={8} />
                          <TextSmall sx={{ ml: '$2', color: '$textPlaceholder' }}>
                            {moment(item?.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                          </TextSmall>
                        </View>
                        <TextSmall sx={{ color: '$textPlaceholder' }}>{item?.intentRefId}</TextSmall>
                      </View>
                    </View>
                  </View>
                </Pressable>
              );
            }}
            scrollEnabled={true}
            showsVerticalScrollIndicator={false}
            sx={{ flex: 1, maxHeight: flatlistHight }}
            keyExtractor={(_, index) => keyGenerator(`account-transaction-${index}`)}
            onEndReachedThreshold={0.05}
            onEndReached={() => {
              if (!isLoadingAccountTransactions) {
                if (nextToken) next();
              }
            }}
          />
          {isLoadingAccountTransactions && (
            <View sx={{ padding: '$5' }}>
              <ActivityIndicator size="large" color={theme.colors?.$disable || '#F0F0F0'} />
            </View>
          )}
        </View>
        {/* <View sx={{ alignItems: 'flex-end', px: '$4' }}>
          <TokenPagination prevPageToken={preToken} nextPageToken={nextToken} onNextPage={next} onPrevPage={prev} />
        </View> */}
      </View>
    </View>
  );
};

enum StyleName {
  contentContainer = 'contentContainer',
  card = 'card',
  table = 'table',
  operations = 'operations',
  filters = 'filters',
  filter_search = 'filter_search'
}

const styles: Record<StyleName, SxProp> = {
  contentContainer: {
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "overview assets"
      "transaction assets"
      "transaction assets"
      `,
    gridTemplateRows: 'repeat(3, auto)',
    height: '100%',
    backgroundColor: '#FFFFFF'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '$4',
    borderRadius: '$radius0',
    boxShadow: 'none',
    paddingY: '$5',
    backgroundColor: '$white'
  },
  table: {
    flexDirection: 'column',
    gap: '$4'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    px: '$4',
    zIndex: 1
  },
  filters: {
    flexDirection: 'row',
    gap: '$5',
    width: '100%'
  },
  filter_search: {
    width: '100%'
  }
};
